import {
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type KeyboardEvent,
  type ReactNode,
} from 'react';
import { SearchIcon, type LucideIcon } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import { usePendingDisplay } from '@/shared/hooks/usePendingDisplay';

import { EmptyState } from './EmptyState';
import { FilterChipGroup, type FilterChipGroupProps } from './FilterChipGroup';
import { listCardCount } from './listCardCount';
import { PendingRegion } from './PendingRegion';
import { RetryButton } from './RetryButton';
import { SectionHeading } from './SectionHeading';

export interface ListCardSearch {
  value: string;
  onChange: (next: string) => void;
  /** Names what is searched: "Search by name or email". */
  placeholder: string;
  /** Accessible name: "Search members". */
  label: string;
}

export interface ListCardEmpty {
  icon?: LucideIcon;
  mark?: ReactNode;
  title?: ReactNode;
  description: ReactNode;
  /** At most one secondary action; never the page's primary action as a filled button. */
  action?: ReactNode;
}

export interface ListCardNoResults {
  /** One sentence naming the search and/or chip: `No one here matches “xyz”.` */
  description: ReactNode;
  /** When set, draws "Clear search" which calls it and returns focus to the field. */
  onClear?: () => void;
  /** Extra control, e.g. Activity's "Show older". */
  extra?: ReactNode;
}

export interface ListCardProps<T extends string = string> {
  title: string;
  count?: number;
  description?: string;
  headingLevel?: 'h2' | 'h3';
  /**
   * The heading's id, when a link elsewhere scrolls to it (People's
   * `members-heading`, spec 193 US6). Generated otherwise.
   */
  headingId?: string;
  /** Trailing content on the title row. */
  action?: ReactNode;
  /** ["member", "members"] */
  noun: readonly [string, string];
  search?: ListCardSearch;
  chips?: FilterChipGroupProps<T>;
  /** Rows after search/chips, and all rows. Drive "N of M things". */
  shown: number;
  total: number;
  state: 'idle' | 'loading' | 'failed' | 'empty';
  /** Drawn by the failed body. */
  failedText?: string;
  onRetry?: () => void;
  empty?: ListCardEmpty;
  noResults?: ListCardNoResults;
  /** Skeleton rows while loading; default 4. */
  skeletonRows?: number;
  /** The rows. Consumers draw the `<ul aria-label>` themselves. */
  children?: ReactNode;
  className?: string;
}

/** How long typing may pause before the new count is announced (research R7). */
const ANNOUNCE_AFTER_MS = 400;

/**
 * The card around a list (spec 226 FR-005..011): the section heading with its
 * count and action, one sentence, one toolbar row — search on the left, chips,
 * "N of M things" at the far right — then the rows, or the state the list is
 * in: a skeleton inside the card after the pending delay, one sentence and
 * Try again, the shared empty state without its dashed border, or the
 * no-results sentence with Clear search while the toolbar stays.
 *
 * Measured from Settings → People, which is the reference every later list
 * matches (research R2). The toolbar is left out entirely when there is
 * neither a search nor chips, so a card without one lines up with a card
 * beside it.
 */
export function ListCard<T extends string = string>({
  title,
  count,
  description,
  headingLevel = 'h2',
  headingId: givenHeadingId,
  action,
  noun,
  search,
  chips,
  shown,
  total,
  state,
  failedText,
  onRetry,
  empty,
  noResults,
  skeletonRows = 4,
  children,
  className,
}: ListCardProps<T>) {
  const generatedId = useId();
  const headingId = givenHeadingId ?? `${generatedId}-heading`;
  const field = useRef<HTMLInputElement>(null);
  const body = useRef<HTMLDivElement>(null);
  const showPlaceholder = usePendingDisplay(state === 'loading');

  // The sentence a screen reader hears, one announcement once typing pauses
  // (FR-007): the drawn count follows every keystroke, the live text lags it.
  const countText = listCardCount(shown, total, noun);
  const [liveText, setLiveText] = useState(countText);
  useEffect(() => {
    const timer = window.setTimeout(() => {
      setLiveText(countText);
    }, ANNOUNCE_AFTER_MS);
    return () => {
      window.clearTimeout(timer);
    };
  }, [countText]);

  // The rows never jump while a filter narrows them (FR-007): the body keeps
  // the height it had with every row drawn until the whole list is back.
  const narrowed = state === 'idle' && shown < total;
  const [fullHeight, setFullHeight] = useState<number | null>(null);
  useLayoutEffect(() => {
    if (state === 'idle' && !narrowed && body.current) {
      setFullHeight(body.current.offsetHeight);
    }
  }, [state, narrowed, shown]);

  const hasToolbar = search !== undefined || chips !== undefined;
  const noneLeft = state === 'idle' && total > 0 && shown === 0;

  const onSearchKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Escape' && search && search.value !== '') {
      event.preventDefault();
      search.onChange('');
    }
  };

  const clearSearch = () => {
    noResults?.onClear?.();
    field.current?.focus();
  };

  return (
    <section
      aria-labelledby={headingId}
      className={cn(
        'border-border bg-card flex min-w-0 flex-col gap-3 rounded-lg border p-4',
        className,
      )}
    >
      <SectionHeading
        as={headingLevel}
        id={headingId}
        count={count}
        action={action}
      >
        {title}
      </SectionHeading>
      {description ? (
        <p className="text-muted-foreground text-sm">{description}</p>
      ) : null}

      {state === 'loading' ? (
        showPlaceholder ? (
          <PendingRegion
            label={`Loading ${title.toLowerCase()}`}
            className="gap-3"
          >
            {hasToolbar ? (
              <Skeleton className="h-8 w-full max-w-xs rounded-lg" />
            ) : null}
            <div className="flex flex-col gap-2">
              {Array.from({ length: skeletonRows }, (_, row) => (
                <div key={row} className="flex items-center gap-3 py-2.5">
                  <Skeleton className="size-8 rounded-full" />
                  <Skeleton
                    className={cn(
                      'h-4 flex-1 rounded-sm',
                      row % 2 === 0 ? 'max-w-md' : 'max-w-sm',
                    )}
                  />
                  <Skeleton className="h-3 w-12 rounded-sm" />
                </div>
              ))}
            </div>
          </PendingRegion>
        ) : null
      ) : null}

      {state === 'failed' ? (
        <div className="flex flex-wrap items-center gap-2">
          <p className="text-muted-foreground text-sm">{failedText}</p>
          {onRetry ? <RetryButton onRetry={onRetry} /> : null}
        </div>
      ) : null}

      {state === 'empty' && empty ? (
        <EmptyState
          icon={empty.icon}
          mark={empty.mark}
          title={empty.title}
          description={empty.description}
          action={empty.action}
          className="border-0 border-none p-6"
        />
      ) : null}

      {state === 'idle' ? (
        <>
          {hasToolbar ? (
            <div className="flex flex-wrap items-center gap-2">
              {search ? (
                <label className="relative min-w-48 flex-1 max-sm:basis-full">
                  <span className="sr-only">{search.label}</span>
                  <SearchIcon
                    aria-hidden="true"
                    strokeWidth={1.5}
                    className="text-muted-foreground pointer-events-none absolute top-1/2 left-2.5 size-4 -translate-y-1/2"
                  />
                  <Input
                    ref={field}
                    type="search"
                    value={search.value}
                    placeholder={search.placeholder}
                    onChange={(event) => {
                      search.onChange(event.target.value);
                    }}
                    onKeyDown={onSearchKeyDown}
                    className="ps-8"
                  />
                </label>
              ) : null}
              {chips ? <FilterChipGroup {...chips} /> : null}
              <p
                aria-live="polite"
                aria-atomic="true"
                // On a phone the count wraps to a line of its own under the
                // search, so it starts at the text's left edge (spec 276).
                className="text-muted-foreground ms-auto text-xs tabular-nums max-md:ms-0"
              >
                <span aria-hidden="true">{countText}</span>
                <span className="sr-only">{liveText}</span>
              </p>
            </div>
          ) : null}
          <div
            ref={body}
            className="flex min-w-0 flex-col"
            style={
              narrowed && fullHeight !== null
                ? { minHeight: fullHeight }
                : undefined
            }
          >
            {noneLeft ? (
              <div className="flex flex-wrap items-center gap-2 py-2">
                <p className="text-muted-foreground text-sm">
                  {noResults?.description}
                </p>
                {noResults?.onClear ? (
                  <Button variant="ghost" size="sm" onClick={clearSearch}>
                    Clear search
                  </Button>
                ) : null}
                {noResults?.extra}
              </div>
            ) : (
              children
            )}
          </div>
        </>
      ) : null}
    </section>
  );
}
